/**
 * The local-disk incremental backup format.
 *
 * WHY SEPARATE FROM THE PORTABLE BACKUP
 * -------------------------------------
 * `backupFormat.ts` is a single JSON snapshot a human can email. That is the
 * wrong shape for automatic disk backup, which must append a change the instant
 * it is committed (without rewriting a growing file), survive being killed
 * mid-write, and recover by reading ONE checkpoint plus a BOUNDED tail — not by
 * replaying the whole history from day one.
 *
 * A FOLDER, not one file:
 *   manifest.json        — header: format, version, device, businesses, the
 *                          checkpoint in force, and the next expected journal
 *                          sequence. Rewritten atomically (temp + rename).
 *   checkpoint-<n>.json  — a FULL snapshot at journal sequence n, in exactly the
 *                          portable-backup shape, so restoreBackup/applyChanges
 *                          consume it unchanged.
 *   journal-<n>.jsonl    — an APPEND-ONLY newline-delimited change log; one JSON
 *                          object per line, a SyncChange plus its sequence.
 *
 * THE RULE THAT MAKES INTERRUPTED WRITES SAFE
 * -------------------------------------------
 * A line is either complete valid JSON or the (at most one) truncated tail.
 * Nothing is rewritten in place, so a half-written line can only ever be the
 * LAST line. The worst case is "the last change was not yet flushed", never "a
 * middle line is garbage". See `parseJournal`.
 *
 * No passwords, tokens, or Supabase keys: the outbox rows this is built from
 * carry business data only, so secrets cannot leak into a user's own drive.
 */

/** Identifies a local-backup folder, so an arbitrary JSON is not mistaken for one. */
export const LOCAL_BACKUP_FORMAT = 'dnr-vyapar-local-backup';
/** Bump only for a breaking on-disk change; readers refuse a newer version. */
export const LOCAL_BACKUP_FORMAT_VERSION = 1;

export const LOCAL_BACKUP_MANIFEST = 'manifest.json';

export interface LocalBackupManifest {
  readonly format: typeof LOCAL_BACKUP_FORMAT;
  readonly formatVersion: number;
  readonly app: 'DNR_Vyapar_2';
  /** Data/schema version the checkpoint was taken at. */
  readonly dataVersion: number;
  /** Stable id of the device that owns this folder. */
  readonly deviceId: string;
  /** Supabase user id that created this backup — prevents one account from
   *  resuming another account's folder after an account switch in the same
   *  browser. Absent on backups taken before this check existed. */
  readonly userId?: string;
  /** Businesses in the checkpoint, for a scoped-restore preview. */
  readonly businessIds: readonly string[];
  /** Journal sequence number the latest checkpoint represents. */
  readonly checkpointSeq: number;
  /** The checkpoint file in force (e.g. `checkpoint-000042.json`). */
  readonly checkpointFile: string;
  /** Next sequence number to append to the journal. */
  readonly nextSeq: number;
  /** ISO timestamp of the last successful flush. Informational. */
  readonly lastBackupAt: string;
  /** Journal entries written since the checkpoint. */
  readonly pendingSinceCheckpoint: number;
  /** Cursor — ISO timestamp of the last outbox entry written to the journal. */
  readonly lastJournaledAt?: string;
  /** Cursor — the changeId at `lastJournaledAt`, to break timestamp ties. */
  readonly lastJournaledChangeId?: string;
  /** Generation identifier — incremented when a new backup generation starts. */
  readonly generation: number;
}

export interface JournalLine {
  /** Monotonic per-folder sequence. Gaps are detectable. */
  readonly seq: number;
  /** A `SyncChange`, verbatim, as it was enqueued. */
  readonly change: unknown;
}

/** A zero-padded filename, so `ls` sorts it and the number is greppable. */
export function checkpointFileName(seq: number): string {
  return `checkpoint-${String(seq).padStart(6, '0')}.json`;
}

export function journalFileName(seq: number): string {
  return `journal-${String(seq).padStart(6, '0')}.jsonl`;
}

export type LocalBackupProblem =
  | 'NOT_A_BACKUP'
  | 'FUTURE_FORMAT'
  | 'UNSUPPORTED_FORMAT'
  | 'CORRUPT_MANIFEST'
  | 'CORRUPT_JOURNAL'
  | 'MISSING_CHECKPOINT'
  | 'SEQ_GAP'

/** Validate a parsed manifest BEFORE anything is read or written. */
export function validateManifest(parsed: unknown): LocalBackupValidation {
  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, problem: 'NOT_A_BACKUP', message: 'The manifest is not an object.' };
  }
  const m = parsed as Partial<LocalBackupManifest>;
  if (m.format !== LOCAL_BACKUP_FORMAT) {
    return { ok: false, problem: 'NOT_A_BACKUP', message: 'This folder is not a DNR Vyapar local backup.' };
  }
  if (m.app !== 'DNR_Vyapar_2') {
    return {
      ok: false,
      problem: 'CORRUPT_MANIFEST',
      message: 'The backup manifest does not identify DNR Vyapar 2.',
    };
  }
  const version = Number(m.formatVersion);
  if (!Number.isFinite(version) || version < 1) {
    return { ok: false, problem: 'UNSUPPORTED_FORMAT', message: 'The backup does not declare a format version.' };
  }
  if (version > LOCAL_BACKUP_FORMAT_VERSION) {
    return {
      ok: false,
      problem: 'FUTURE_FORMAT',
      message: `This backup was written by a newer version of DNR Vyapar (format ${version}). Update the app before restoring it.`,
    };
  }
  if (typeof m.deviceId !== 'string' || !m.deviceId) {
    return { ok: false, problem: 'CORRUPT_MANIFEST', message: 'The backup manifest has no device id.' };
  }
  if (!Number.isFinite(Number(m.checkpointSeq)) || !Number.isFinite(Number(m.nextSeq))) {
    return { ok: false, problem: 'CORRUPT_MANIFEST', message: 'The backup manifest has no sequence numbers.' };
  }
  if (typeof m.checkpointFile !== 'string' || !m.checkpointFile) {
    return { ok: false, problem: 'CORRUPT_MANIFEST', message: 'The backup manifest names no checkpoint file.' };
  }
  if (Number(m.nextSeq) < Number(m.checkpointSeq)) {
    return {
      ok: false,
      problem: 'CORRUPT_MANIFEST',
      message: 'The backup manifest is inconsistent (next sequence precedes the checkpoint).',
    };
  }
    return {
      ...OK,
      manifest: {
        format: LOCAL_BACKUP_FORMAT,
        formatVersion: version,
        app: 'DNR_Vyapar_2',
        dataVersion: Number(m.dataVersion ?? 0),
        deviceId: m.deviceId,
        userId: typeof m.userId === 'string' ? m.userId : undefined,
        businessIds: Array.isArray(m.businessIds) ? m.businessIds : [],
        checkpointSeq: Number(m.checkpointSeq),
        checkpointFile: m.checkpointFile,
        nextSeq: Number(m.nextSeq),
        lastBackupAt: typeof m.lastBackupAt === 'string' ? m.lastBackupAt : '',
        pendingSinceCheckpoint: Number(m.pendingSinceCheckpoint ?? 0),
        lastJournaledAt: typeof m.lastJournaledAt === 'string' ? m.lastJournaledAt : '',
        lastJournaledChangeId:
          typeof m.lastJournaledChangeId === 'string' ? m.lastJournaledChangeId : '',
        generation: Number(m.generation ?? 0),
      },
    };
}

export interface LocalBackupValidation {
  readonly ok: boolean;
  readonly problem?: LocalBackupProblem;
  readonly message?: string;
  readonly manifest?: LocalBackupManifest;
}

const OK: LocalBackupValidation = { ok: true };

export interface JournalParseResult {
  readonly lines: readonly JournalLine[];
  /** True when the final line was a partial write (an interrupted flush). */
  readonly truncated: boolean;
  readonly problem?: LocalBackupProblem;
  readonly message?: string;
}

/**
 * Parse a journal file's text into validated lines.
 *
 * A truncated FINAL line is not an error: it is the expected result of a write
 * interrupted mid-flush, so it is reported as `truncated` and dropped. A
 * non-final line that fails to parse IS corruption — a hole in the middle of the
 * log cannot be replayed safely, and replaying around it would produce a
 * database that silently skips a change.
 */
export function parseJournal(text: string): JournalParseResult {
  const trimmed = text.replace(/\n+$/, '');
  if (trimmed === '') return { lines: [], truncated: false };
  const raw = trimmed.split('\n');
  const lines: JournalLine[] = [];
  for (let i = 0; i < raw.length; i++) {
    const line = raw[i];
    const isLast = i === raw.length - 1;
    let parsed: unknown;
    try {
      parsed = JSON.parse(line ?? '');
    } catch {
      // Only the final line may be a partial write.
      if (isLast) return { lines, truncated: true };
      return {
        lines: [],
        problem: 'CORRUPT_JOURNAL',
        message: `The journal has an unreadable entry at line ${i + 1}.`,
        truncated: false,
      };
    }
    const seq = (parsed as { seq?: unknown })?.seq;
    if (typeof seq !== 'number' || !Number.isFinite(seq)) {
      if (isLast) return { lines, truncated: true };
      return {
        lines: [],
        problem: 'CORRUPT_JOURNAL',
        message: `The journal entry at line ${i + 1} has no sequence number.`,
        truncated: false,
      };
    }
    lines.push({ seq, change: (parsed as { change?: unknown }).change });
  }
  return { lines, truncated: false };
}

/**
 * Journal sequences must be strictly increasing with no gaps from `fromSeq`.
 *
 * A gap means an entry was lost, and replaying the rest would produce a database
 * that skips a change. Detected here so a restore can refuse rather than guess.
 */
export function journalSequencesOk(lines: readonly JournalLine[], fromSeq: number): boolean {
  let expected = fromSeq;
  for (const line of lines) {
    if (line.seq !== expected) return false;
    expected += 1;
  }
  return true;
}

